"use client";

import { loadVWOSmartCode } from "@aspendental/web-shared-utils";
import { Children, isValidElement, useEffect, type ReactElement, type ReactNode } from "react";
import { VWOScript } from "vwo-smartcode-nextjs";

interface VWOSmartCodeProps {
	accountId: string;
	version?: 2.2 | 3.0;
}

/**
 * Pulls the inline VWO SmartCode string out of the official `vwo-smartcode-nextjs`
 * component instead of hand-copying it, so we stay on the packaged, versioned code.
 * Returns null for the SYNC variant (which renders a `src` script with no inline body).
 */
export const extractVwoSmartCode = (accountId: string, version?: 2.2 | 3.0): string | null => {
	// Force the ASYNC variant: only it renders the inline `dangerouslySetInnerHTML`
	// snippet we hand to `loadVWOSmartCode`. The SYNC variant (which `version={3.0}`
	// selects by default) renders a bare `<script src>` with no inline body, so
	// extraction would return null and VWO would silently never load. The inline
	// snippet still bootstraps the correct versioned tag.
	const rendered = (
		VWOScript as unknown as (props: {
			accountId: string;
			version?: 2.2 | 3.0;
			type?: "ASYNC" | "SYNC";
		}) => ReactElement | null
	)({ accountId, version, type: "ASYNC" });

	if (!isValidElement(rendered)) {
		return null;
	}

	for (const child of Children.toArray((rendered.props as { children?: ReactNode }).children)) {
		if (isValidElement(child) && child.type === "script") {
			const html = (child.props as { dangerouslySetInnerHTML?: { __html?: string } })
				.dangerouslySetInnerHTML?.__html;
			if (typeof html === "string" && html.length > 0) {
				return html;
			}
		}
	}

	return null;
};

/**
 * Loads the VWO SmartCode on the client, gated on OneTrust Performance/Analytics
 * (group 2) consent via the shared `loadVWOSmartCode` helper. Consent detection and
 * consent-change watching (`OneTrustGroupsUpdated`) live in
 * `@aspendental/web-shared-utils`, so the "which categories have consent" decision is
 * owned in one place and not duplicated per app. Renders nothing.
 *
 * The loader must be 0.5.17 or later. On a first visit OneTrust fires
 * `OneTrustGroupsUpdated` a moment before it writes the groups into the
 * `OptanonConsent` cookie, so a check made only at that event sees no consent and
 * VWO never loads. From 0.5.17 the loader also re-reads the cookie every 250 ms for 5 s, which
 * catches the groups when they land; `cleanup` stops that poll on unmount.
 */
export const VWOSmartCode = ({ accountId, version }: VWOSmartCodeProps): null => {
	useEffect(() => {
		if (!accountId) {
			return;
		}

		const snippet = extractVwoSmartCode(accountId, version);
		if (!snippet) {
			return;
		}

		const { cleanup } = loadVWOSmartCode({ snippet });
		return cleanup;
	}, [accountId, version]);

	return null;
};
